import { useState, type FormEvent } from 'react';

import type { CurrentUser, TgaProviderSummary } from '../domain-types';

export function ProviderSelectionPanel({
  results,
  selected,
  busy,
  onSelect,
  onConfirm,
}: {
  results: TgaProviderSummary[];
  selected: TgaProviderSummary | null;
  busy: boolean;
  onSelect: (provider: TgaProviderSummary) => void;
  onConfirm: () => Promise<void>;
}) {
  return (
    <>
      <div className="provider-results">
        {results.map((provider) => (
          <button
            className={selected?.providerCode === provider.providerCode ? 'selected' : ''}
            key={provider.providerCode}
            onClick={() => onSelect(provider)}
            type="button"
          >
            <strong>{provider.legalName}</strong>
            <span>RTO {provider.providerCode}</span>
            {provider.tradingNames.length > 0 ? (
              <small>{provider.tradingNames.join(', ')}</small>
            ) : null}
          </button>
        ))}
      </div>
      {selected ? (
        <div className="provider-confirm">
          <p>
            You are selecting <strong>{selected.legalName}</strong> (RTO {selected.providerCode}).
            The current TGA Chief Executive contact will be asked to verify this association.
          </p>
          <button disabled={busy} type="button" onClick={() => void onConfirm()}>
            {busy ? 'Creating organisation…' : 'Confirm provider'}
          </button>
        </div>
      ) : null}
    </>
  );
}

export function ProviderOnboarding({
  apiUrl,
  accessToken,
  currentUser,
  onCompleted,
  id,
}: {
  apiUrl: string;
  accessToken: string;
  currentUser: CurrentUser;
  onCompleted: () => void;
  id?: string;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<TgaProviderSummary[]>([]);
  const [selected, setSelected] = useState<TgaProviderSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const authorization = { authorization: `Bearer ${accessToken}` };

  const search = async (event: FormEvent) => {
    event.preventDefault();
    if (query.trim().length < 2) return;
    setBusy(true);
    setMessage('');
    const response = await fetch(
      `${apiUrl}/v1/tga/providers/search?q=${encodeURIComponent(query.trim())}`,
      { headers: authorization },
    );
    setBusy(false);
    if (!response.ok) {
      setMessage('Provider search is temporarily unavailable. Please try again shortly.');
      return;
    }
    const body = (await response.json()) as { items: TgaProviderSummary[] };
    setResults(body.items);
    if (body.items.length === 0) setMessage('No current RTO matched that code or name.');
  };

  const claim = async () => {
    if (!selected) return;
    setBusy(true);
    setMessage('');
    const response = await fetch(`${apiUrl}/v1/onboarding/provider-selection`, {
      method: 'POST',
      headers: { ...authorization, 'content-type': 'application/json' },
      body: JSON.stringify({ providerCode: selected.providerCode }),
    });
    const body = (await response.json()) as {
      outcome?: 'tenant_created' | 'access_requested';
      message?: string;
    };
    setBusy(false);
    if (!response.ok) {
      setMessage(body.message ?? 'The provider could not be selected.');
      return;
    }
    if (body.outcome === 'access_requested') {
      setSelected(null);
      setResults([]);
      setMessage(
        'This RTO already has a quality.vet organisation. Your access request is awaiting an administrator.',
      );
      return;
    }
    onCompleted();
  };

  return (
    <main className="provider-onboarding-page" id={id} tabIndex={id ? -1 : undefined}>
      <section className="provider-onboarding-card">
        <a className="brand" href="/">
          quality<span>.vet</span>
        </a>
        <p className="eyebrow">Set up your organisation</p>
        <h1>Select your registered provider</h1>
        <p>
          Search the training.gov.au cache by registration code, legal name or trading name. Current
          RTOs can be selected.
        </p>
        {currentUser.pendingAccessRequest?.status === 'pending' ? (
          <div className="provider-notice">
            Access to RTO {currentUser.pendingAccessRequest.providerCode} is awaiting approval from
            an existing tenant administrator.
          </div>
        ) : null}
        <form className="provider-search" onSubmit={(event) => void search(event)}>
          <label>
            Registration code or provider name
            <input
              minLength={2}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="For example, 12345 or Example Training"
            />
          </label>
          <button disabled={busy || query.trim().length < 2} type="submit">
            {busy ? 'Searching…' : 'Search providers'}
          </button>
        </form>
        {message ? (
          <p aria-atomic="true" className="provider-notice" role="status" aria-live="polite">
            {message}
          </p>
        ) : null}
        <ProviderSelectionPanel
          results={results}
          selected={selected}
          busy={busy}
          onSelect={setSelected}
          onConfirm={claim}
        />
      </section>
    </main>
  );
}
